import React, { useState, useContext, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation, Link } from 'react-router-dom';
import DashboardLayout from '../../components/DashboardLayout';
import ReviewModal from '../../components/ReviewModal';
import SecurityPage from './SecurityPage';
import MenteeReferPage from './MenteeReferPage';
import { AppContext } from '../../App';
import { MENTEE_DASHBOARD, MENTORS, convertPrice, getAvatarUrl } from '../../data/mockData';
import { bookingService, apiClient } from '../../services';
import DashboardSkeleton from '../../components/DashboardSkeleton';
import { useFollow } from '../../context/FollowContext';
import { useAuth } from '../../context/AuthContext';
import { useEmail } from '../../context/EmailContext';
import { EMAIL_EVENTS } from '../../context/EmailContext';
import { FollowButton, FavoriteButton } from '../../components/FollowButton';
import { MeetingJoinInline } from '../../components/MeetingJoinButton';
import { getMentorAppStatus, setMentorAppStatus, canReapply, reapplyUnlockDate } from '../../utils/mentorApp';
import { COURSES, MY_ENROLLMENTS } from '../../features/certifications/data';
import ProfilePhotoUpload from '../../components/ProfilePhotoUpload';

// Module-level fallback data (used by sub-components)
const D = MENTEE_DASHBOARD;

function Av({ seed, bg, init, size=38 }) {
  return (
    <div style={{width:size,height:size,borderRadius:10,overflow:'hidden',flexShrink:0,background:`#${bg}`,position:'relative',display:'flex',alignItems:'center',justifyContent:'center'}}>
      <img src={getAvatarUrl(seed,bg,size)} alt={init} style={{width:'100%',height:'100%',objectFit:'cover'}} onError={e=>e.target.style.display='none'}/>
      <span style={{position:'absolute',fontWeight:800,fontSize:'.72rem',color:'#fff'}}>{init}</span>
    </div>
  );
}

const EXPERTISE_OPTIONS = [
  'React / Frontend', 'Node.js / Backend', 'AI / Machine Learning',
  'Flutter / Mobile', 'Cloud & DevOps', 'Cybersecurity', 'UI/UX Design', 'Data Science',
];

const fieldStyle = {
  width:'100%', padding:'10px 12px', borderRadius:10,
  border:'1.5px solid #e5e7eb', fontSize:'.88rem', outline:'none', boxSizing:'border-box',
};
const errStyle = { fontSize:'.72rem', color:'#ef4444', marginTop:4 };
const labelStyle = { display:'block', fontSize:'.78rem', fontWeight:700, color:'#374151', marginBottom:5 };
const reqStar = <span style={{color:'#ef4444'}}>*</span>;

export function BecomeMentorModal({ user, onClose }) {
  const isMock = process.env.REACT_APP_MOCK !== 'false';
  const { sendEmail } = useEmail();
  const [skills, setSkills] = useState([]);        // array of selected preset skills
  const [otherSkill, setOtherSkill] = useState(''); // free-text "Other" skill
  const [exp, setExp] = useState('');
  const [linkedin, setLinkedin] = useState('');
  const [bio, setBio] = useState('');
  const [errors, setErrors] = useState({});
  const [step, setStep] = useState('form');
  const [submitting, setSubmitting] = useState(false);

  const toggleSkill = skill => setSkills(prev =>
    prev.includes(skill) ? prev.filter(s => s !== skill) : [...prev, skill]
  );

  const validate = () => {
    const e = {};
    const allSkills = [...skills, ...(otherSkill.trim() ? [otherSkill.trim()] : [])];
    if (!allSkills.length)          e.skills   = 'Select at least one skill.';
    if (!exp)                       e.exp      = 'Please select your experience range.';
    if (!linkedin.trim())           e.linkedin = 'LinkedIn URL is required.';
    if (!bio.trim())                e.bio      = 'A brief bio is required.';
    return e;
  };

  const handleSubmit = async () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    setErrors({});
    setSubmitting(true);
    const allSkills = [...skills, ...(otherSkill.trim() ? [otherSkill.trim()] : [])];

    // Fire email first — always sends regardless of API result
    sendEmail(EMAIL_EVENTS.MENTOR_APPLICATION, {
      name:        user?.name,
      email:       user?.email,
      profileUrl:  'https://www.devbrains.in/dashboard/mentor/profile',
      platformUrl: 'www.devbrains.in',
    });

    if (!isMock) {
      try {
        // POST /mentors/apply creates a new Mentor document in the database
        // (PUT /mentors/me/profile would fail — the user has no Mentor record yet)
        await apiClient.post('/mentors/apply', {
          experience: parseInt(exp) || 0,
          skills:     allSkills,
          company:    user?.company || '',
          bio,
          linkedin,
        });
      } catch (apiErr) {
        // ALREADY_APPLIED or ALREADY_APPROVED — not a fatal error, continue to success screen
        const code = apiErr?.code || '';
        if (code === 'ALREADY_APPROVED') {
          setErrors({ form: 'You are already an approved mentor!' });
          setSubmitting(false);
          return;
        }
        // For any other API error, still save locally so admin can see it
        console.warn('[BecomeMentor] API error, falling back to localStorage:', apiErr?.message);
      }
    }

    setSubmitting(false);
    // Always persist locally so the mentee dashboard can show the pending status
    // and the admin Approvals page can display in-app applications (mock mode)
    setMentorAppStatus(user?.id, {
      status:      'pending',
      submittedAt: Date.now(),
      name:        user?.name        || '',
      email:       user?.email       || '',
      headline:    user?.headline    || '',
      company:     user?.company     || '',
      experience:  exp               || '',
      skills:      allSkills,
      bio:         bio               || '',
      linkedin:    linkedin          || '',
      initials:    user?.initials    || '',
      avatarBg:    user?.avatarBg    || '1DB954',
    });
    setStep('success');
  };

  const overlayStyle = {
    position:'fixed', inset:0, background:'rgba(0,0,0,.48)', zIndex:9999,
    display:'flex', alignItems:'center', justifyContent:'center', padding:16, overflowY:'auto',
  };
  const boxStyle = {
    background:'#fff', borderRadius:20, padding:32, width:'100%', maxWidth:500,
    boxShadow:'0 24px 60px rgba(0,0,0,.2)', position:'relative', margin:'auto',
  };

  return (
    <div style={overlayStyle} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={boxStyle}>
        <button onClick={onClose} style={{position:'absolute',top:16,right:16,background:'none',border:'none',fontSize:'1.25rem',cursor:'pointer',color:'#9ca3af',lineHeight:1}}>✕</button>

        {step === 'success' ? (
          <div style={{textAlign:'center',padding:'8px 0 12px'}}>
            <div style={{fontSize:52,marginBottom:12}}>🎉</div>
            <h2 style={{fontWeight:900,fontSize:'1.4rem',color:'#111827',marginBottom:10}}>Thanks for Your Interest!</h2>
            <p style={{color:'#6b7280',lineHeight:1.75,marginBottom:8}}>
              Hi <strong>{user?.name?.split(' ')[0]}</strong>, your mentor application has been received.
            </p>
            <p style={{color:'#6b7280',lineHeight:1.75,marginBottom:24}}>
              We're reviewing your profile and you'll get a response soon — check your inbox at <strong>{user?.email}</strong>.
            </p>
            <div style={{background:'#f0fdf4',border:'1px solid #bbf7d0',borderRadius:12,padding:'14px 16px',marginBottom:24,textAlign:'left'}}>
              <p style={{margin:'0 0 6px',fontWeight:700,color:'#166534',fontSize:'.82rem'}}>While you wait:</p>
              {['Complete your profile — add work experience','Set your availability and pricing','Add your GitHub, portfolio links'].map(t=>(
                <p key={t} style={{margin:'3px 0',color:'#166534',fontSize:'.78rem'}}>✅ {t}</p>
              ))}
            </div>
            <button className="btn btn-primary" style={{width:'100%',justifyContent:'center',padding:'12px'}} onClick={onClose}>Done</button>
          </div>
        ) : (
          <>
            <div style={{marginBottom:20}}>
              <div style={{fontSize:'.72rem',fontWeight:700,color:'#16a34a',letterSpacing:'.05em',textTransform:'uppercase',marginBottom:4}}>Become a Mentor</div>
              <h2 style={{fontWeight:900,fontSize:'1.25rem',color:'#111827',marginBottom:4}}>Share your expertise</h2>
              <p style={{fontSize:'.82rem',color:'#6b7280'}}>Your name and email are already saved — just fill in the details below.</p>
            </div>

            <div style={{background:'#f9fafb',border:'1px solid #e5e7eb',borderRadius:12,padding:'12px 14px',marginBottom:20,display:'flex',gap:12,alignItems:'center'}}>
              <div style={{width:38,height:38,borderRadius:10,background:'#16a34a',display:'flex',alignItems:'center',justifyContent:'center',color:'#fff',fontWeight:800,fontSize:'.9rem',flexShrink:0}}>
                {user?.name?.charAt(0) || '?'}
              </div>
              <div>
                <div style={{fontWeight:700,fontSize:'.88rem',color:'#111827'}}>{user?.name}</div>
                <div style={{fontSize:'.78rem',color:'#6b7280'}}>{user?.email}</div>
              </div>
            </div>

            <div style={{display:'flex',flexDirection:'column',gap:16}}>

              {/* Skills — multi-select chips */}
              <div>
                <label style={labelStyle}>Skills / Expertise {reqStar}</label>
                <div style={{display:'flex',flexWrap:'wrap',gap:8,marginBottom:8}}>
                  {EXPERTISE_OPTIONS.map(o => {
                    const active = skills.includes(o);
                    return (
                      <button key={o} type="button" onClick={() => { toggleSkill(o); setErrors(p=>({...p,skills:undefined})); }}
                        style={{padding:'6px 13px',borderRadius:20,border:`1.5px solid ${active?'#16a34a':'#e5e7eb'}`,background:active?'#f0fdf4':'#f9fafb',color:active?'#166534':'#374151',fontSize:'.78rem',fontWeight:active?700:500,cursor:'pointer',transition:'all .12s'}}>
                        {active && <span style={{marginRight:4}}>✓</span>}{o}
                      </button>
                    );
                  })}
                </div>
                <input value={otherSkill} onChange={e=>{setOtherSkill(e.target.value);setErrors(p=>({...p,skills:undefined}));}}
                  placeholder="Other skill (e.g. Blockchain, Go, Product Management…)"
                  style={{...fieldStyle,background:'#fff'}} />
                {errors.skills && <p style={errStyle}>{errors.skills}</p>}
              </div>

              {/* Experience */}
              <div>
                <label style={labelStyle}>Years of Experience {reqStar}</label>
                <select value={exp} onChange={e=>{setExp(e.target.value);setErrors(p=>({...p,exp:undefined}));}}
                  style={{...fieldStyle,color:exp?'#111827':'#9ca3af',background:'#fff'}}>
                  <option value="">Select experience range</option>
                  <option>3–5 years</option>
                  <option>5–10 years</option>
                  <option>10+ years</option>
                </select>
                {errors.exp && <p style={errStyle}>{errors.exp}</p>}
              </div>

              {/* LinkedIn */}
              <div>
                <label style={labelStyle}>LinkedIn Profile URL {reqStar}</label>
                <input value={linkedin} onChange={e=>{setLinkedin(e.target.value);setErrors(p=>({...p,linkedin:undefined}));}}
                  placeholder="linkedin.com/in/yourprofile"
                  style={{...fieldStyle, borderColor: errors.linkedin ? '#fca5a5' : '#e5e7eb'}} />
                {errors.linkedin && <p style={errStyle}>{errors.linkedin}</p>}
              </div>

              {/* Bio */}
              <div>
                <label style={labelStyle}>Brief Bio {reqStar}</label>
                <textarea value={bio} onChange={e=>{setBio(e.target.value);setErrors(p=>({...p,bio:undefined}));}} rows={3}
                  placeholder="Tell us about your background and why you want to mentor…"
                  style={{...fieldStyle,resize:'vertical', borderColor: errors.bio ? '#fca5a5' : '#e5e7eb'}} />
                {errors.bio && <p style={errStyle}>{errors.bio}</p>}
              </div>

              <button className="btn btn-primary" disabled={submitting}
                style={{width:'100%',justifyContent:'center',padding:'13px',borderRadius:10,marginTop:2}}
                onClick={handleSubmit}>
                {submitting ? 'Submitting…' : 'Submit Application →'}
              </button>
              <p style={{fontSize:'.72rem',color:'#9ca3af',textAlign:'center',margin:0}}>
                No commitment required. We'll respond within 48 hours.
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Overview({ upcoming: _up, past: _past, loading = false, user }) {
  const isMock   = process.env.REACT_APP_MOCK !== 'false';
  const upcoming = _up   || (isMock ? D.upcomingSessions : []);
  const past     = _past || (isMock ? D.pastSessions     : []);
  const nav = useNavigate();
  const { currency } = useContext(AppContext);
  const [mentorModalOpen, setMentorModalOpen] = useState(false);
  // Derive stats from real session data
  const completedCnt  = past.filter(s => s.status === 'completed').length;
  const cancelledCnt  = past.filter(s => s.status === 'cancelled').length;
  const totalSpent    = past.reduce((sum, s) => sum + (s.priceINR || 0), 0);
  const uniqueMentors = new Set(past.map(s => s.mentorId || s.mentor)).size || 0;
  return (
    <div>
      {mentorModalOpen && <BecomeMentorModal user={user} onClose={() => setMentorModalOpen(false)} />}
      <h1 className="db-page-title">My Learning Hub 🎓</h1><p className="db-page-sub">Track your progress and upcoming sessions.</p>
      <div className="db-stats-grid-4" style={{marginBottom:24}}>
        {[{icon:'📅',label:'Total Sessions',v:(completedCnt+upcoming.length),sub:`${upcoming.length} upcoming`},{icon:'✅',label:'Completed',v:completedCnt,sub:`${cancelledCnt} cancelled`},{icon:'💰',label:'Total Spent',v:convertPrice(totalSpent,currency),sub:'on sessions'},{icon:'⭐',label:'Mentors',v:uniqueMentors,sub:'worked with'}].map(s=>(<div key={s.label} className="db-stat-card"><div className="dsc-icon">{s.icon}</div><div className="dsc-label">{s.label}</div><div className="dsc-value">{s.v}</div><div className="dsc-sub">{s.sub}</div></div>))}
      </div>

      {/* Promo CTAs */}
      <div className="db-promo-grid">
        {/* Find a Mentor card */}
        <div className="db-promo-card" style={{background:'linear-gradient(135deg,#eff6ff,#dbeafe)',border:'1px solid #bfdbfe'}}>
          <div>
            <div style={{fontSize:'2rem',marginBottom:8}}>🔍</div>
            <h3 style={{fontSize:'1rem',fontWeight:800,color:'#1a1a2e',margin:'0 0 6px'}}>Find a Mentor or Expert</h3>
            <p style={{fontSize:'.84rem',color:'#374151',margin:0,lineHeight:1.6}}>Get personalized solutions, career advice, and expert guidance — book a session in minutes.</p>
          </div>
          <Link to="/mentors" style={{display:'inline-flex',alignItems:'center',gap:6,background:'#2563eb',color:'#fff',padding:'10px 18px',borderRadius:10,fontWeight:700,fontSize:'.84rem',textDecoration:'none',width:'fit-content'}}>Browse Mentors →</Link>
        </div>
        {/* Become a Mentor card */}
        {(() => {
          const app = getMentorAppStatus(user?.id);
          if (app?.status === 'pending') {
            return (
              <div className="db-promo-card" style={{background:'linear-gradient(135deg,#fffbeb,#fef3c7)',border:'1px solid #fde68a'}}>
                <div>
                  <div style={{fontSize:'2rem',marginBottom:8}}>⏳</div>
                  <h3 style={{fontSize:'1rem',fontWeight:800,color:'#92400e',margin:'0 0 6px'}}>Application Under Review</h3>
                  <p style={{fontSize:'.84rem',color:'#b45309',margin:0,lineHeight:1.6}}>
                    Your mentor application is being reviewed. Our team will respond within 24–48 hours.
                  </p>
                  {app.submittedAt && (
                    <p style={{fontSize:'.75rem',color:'#d97706',marginTop:6,fontWeight:600}}>
                      Submitted on {new Date(app.submittedAt).toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'})}
                    </p>
                  )}
                </div>
                <span style={{display:'inline-flex',alignItems:'center',gap:6,background:'#fef3c7',border:'1px solid #fde68a',color:'#92400e',padding:'8px 16px',borderRadius:10,fontWeight:800,fontSize:'.78rem'}}>PENDING</span>
              </div>
            );
          }
          if (app?.status === 'rejected' && !canReapply(app)) {
            return (
              <div className="db-promo-card" style={{background:'linear-gradient(135deg,#fef2f2,#fee2e2)',border:'1px solid #fecaca'}}>
                <div>
                  <div style={{fontSize:'2rem',marginBottom:8}}>❌</div>
                  <h3 style={{fontSize:'1rem',fontWeight:800,color:'#991b1b',margin:'0 0 6px'}}>Application Not Approved</h3>
                  <p style={{fontSize:'.84rem',color:'#b91c1c',margin:0,lineHeight:1.6}}>
                    You can reapply on <strong>{reapplyUnlockDate(app)}</strong>.
                  </p>
                </div>
                <span style={{display:'inline-flex',alignItems:'center',gap:6,background:'#fee2e2',border:'1px solid #fecaca',color:'#991b1b',padding:'8px 16px',borderRadius:10,fontWeight:800,fontSize:'.78rem'}}>REJECTED</span>
              </div>
            );
          }
          return (
            <div className="db-promo-card" style={{background:'linear-gradient(135deg,#f5f3ff,#ede9fe)',border:'1px solid #ddd6fe'}}>
              <div>
                <div style={{fontSize:'2rem',marginBottom:8}}>💼</div>
                <h3 style={{fontSize:'1rem',fontWeight:800,color:'#1a1a2e',margin:'0 0 6px'}}>Become a Mentor</h3>
                <p style={{fontSize:'.84rem',color:'#374151',margin:0,lineHeight:1.6}}>Share your expertise and <strong>start earning within 30 minutes</strong> of completing your mentor profile.</p>
              </div>
              <button onClick={() => setMentorModalOpen(true)} style={{display:'inline-flex',alignItems:'center',gap:6,background:'#7c3aed',color:'#fff',padding:'10px 18px',borderRadius:10,fontWeight:700,fontSize:'.84rem',border:'none',cursor:'pointer',width:'fit-content'}}>
                {app?.status === 'rejected' ? 'Reapply Now →' : 'Start Earning →'}
              </button>
            </div>
          );
        })()}
      </div>

      <div className="db-grid-2" style={{marginBottom:24}}>
        <div className="db-card">
          <div className="db-section-header"><span className="db-section-title">📅 Upcoming Sessions</span><button className="db-section-action" onClick={()=>nav('/dashboard/mentee/sessions')}>View all →</button></div>
          {upcoming.length===0?<div className="db-empty"><div className="db-empty-ico">📅</div><h3>No upcoming sessions</h3><p>Book a session with a mentor to get started.</p><Link to="/mentors" className="btn btn-primary" style={{marginTop:14}}>Find a Mentor</Link></div>:upcoming.map(s=>(
            <div key={s.id} className="session-row">
              <Av seed={s.mentorAvatar} bg={s.mentorBg} init={s.mentorInit}/>
              <div className="sr-info"><div className="sr-name">{s.mentor}</div><div className="sr-meta">{s.type} · {s.date} at {s.time}</div><div style={{fontSize:'.7rem',color:'#9ca3af'}}>{s.topic}</div></div>
              <div className="sr-right"><span className={`st-badge st-${s.status}`}>{s.status}</span><div className="sr-date">{convertPrice(s.priceINR,currency)}</div></div>
            </div>
          ))}
          {upcoming.length>0&&<div style={{marginTop:14,paddingTop:12,borderTop:'1px solid #f3f4f6',display:'flex',gap:8}}>
            <MeetingJoinInline platform={upcoming[0].meetingPlatform || 'google_meet'} url={upcoming[0].meetLink} date={upcoming[0].date} time={upcoming[0].time} />
            <button className="btn btn-outline btn-sm">✎ Reschedule</button>
          </div>}
        </div>
        <div className="db-card">
          <div className="db-section-header"><span className="db-section-title">🎯 Quick Actions</span></div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
            {[{icon:'🔍',label:'Find Mentors',path:'/mentors',color:'#16a34a'},{icon:'📅',label:'My Sessions',path:'/dashboard/mentee/sessions',color:'#2563eb'},{icon:'💳',label:'Billing',path:'/dashboard/mentee/billing',color:'#7c3aed'},{icon:'🎫',label:'Support',path:'/dashboard/mentee/support',color:'#d97706'}].map(a=>(
              <Link key={a.label} to={a.path} style={{display:'flex',flexDirection:'column',alignItems:'center',gap:6,padding:'18px 12px',background:'#f9fafb',border:'1px solid #e5e7eb',borderRadius:14,textDecoration:'none',transition:'all .15s'}}
                onMouseEnter={e=>{e.currentTarget.style.borderColor=a.color;e.currentTarget.style.background=a.color+'11';}}
                onMouseLeave={e=>{e.currentTarget.style.borderColor='#e5e7eb';e.currentTarget.style.background='#f9fafb';}}>
                <span style={{fontSize:'1.5rem'}}>{a.icon}</span>
                <span style={{fontSize:'.78rem',fontWeight:800,color:'#374151'}}>{a.label}</span>
              </Link>
            ))}
          </div>
        </div>
      </div>
      <div className="db-card">
        <div className="db-section-header"><span className="db-section-title">📚 Recent Sessions</span><button className="db-section-action" onClick={()=>nav('/dashboard/mentee/sessions')}>View all →</button></div>
        <div className="db-table-wrap"><table className="db-table"><thead><tr><th>Mentor</th><th>Type</th><th>Date</th><th>Amount</th><th>Status</th><th>Rating</th></tr></thead><tbody>
          {past.map(s=>(<tr key={s.id}><td><div style={{display:'flex',alignItems:'center',gap:8}}><div className="db-mini-av" style={{background:`#${s.mentorBg}`}}>{s.mentorInit}</div>{s.mentor}</div></td><td>{s.type}</td><td>{s.date}</td><td>{convertPrice(s.priceINR,currency)}</td><td><span className={`st-badge st-${s.status}`}>{s.status}</span></td><td>{s.rated?'⭐'.repeat(s.myRating):(s.canRate?<button className="btn btn-outline btn-sm" style={{padding:'3px 10px',fontSize:'.7rem'}}>Rate Now</button>:'—')}</td></tr>))}
        </tbody></table></div>
      </div>

      {/* Become a Mentor promo banner — hidden once applied */}
      {(() => {
        const app = getMentorAppStatus(user?.id);
        if (app?.status === 'pending' || (app?.status === 'rejected' && !canReapply(app))) return null;
        return (
          <div style={{marginTop:24,borderRadius:20,background:'linear-gradient(135deg,#16a34a 0%,#15803d 100%)',padding:'28px 32px',display:'flex',alignItems:'center',justifyContent:'space-between',gap:20,flexWrap:'wrap'}}>
            <div>
              <div style={{fontSize:'.72rem',fontWeight:700,color:'rgba(255,255,255,.7)',letterSpacing:'.06em',textTransform:'uppercase',marginBottom:6}}>For You</div>
              <h3 style={{fontWeight:900,fontSize:'1.15rem',color:'#fff',margin:'0 0 6px'}}>Ready to share what you know?</h3>
              <p style={{fontSize:'.84rem',color:'rgba(255,255,255,.82)',margin:0,maxWidth:420,lineHeight:1.6}}>
                Turn your experience into income. Join 2,400+ mentors on DevBrains — set your own rates and hours.
              </p>
            </div>
            <button
              onClick={() => setMentorModalOpen(true)}
              style={{flexShrink:0,padding:'12px 24px',borderRadius:12,border:'2px solid rgba(255,255,255,.5)',background:'rgba(255,255,255,.15)',color:'#fff',fontWeight:800,fontSize:'.88rem',cursor:'pointer',backdropFilter:'blur(4px)',transition:'all .15s',whiteSpace:'nowrap'}}
              onMouseEnter={e=>{e.currentTarget.style.background='rgba(255,255,255,.28)';e.currentTarget.style.borderColor='rgba(255,255,255,.8)';}}
              onMouseLeave={e=>{e.currentTarget.style.background='rgba(255,255,255,.15)';e.currentTarget.style.borderColor='rgba(255,255,255,.5)';}}>
              {app?.status === 'rejected' ? 'Reapply Now →' : 'Become a Mentor →'}
            </button>
          </div>
        );
      })()}
    </div>
  );
}

function MySessions({ upcoming: _up, past: _past, loading = false }) {
  const isMockSess = process.env.REACT_APP_MOCK !== 'false';
  const upcoming = _up   || (isMockSess ? D.upcomingSessions : []);
  const past     = _past || (isMockSess ? D.pastSessions     : []);
  const [tab,setTab]=useState('upcoming');
  const { currency } = useContext(AppContext);
  const [reviewModal, setReviewModal] = useState(null);  // session object or null
  const [reviewedIds, setReviewedIds] = useState([]);    // sessions already reviewed this session
  const [sessionList, setSessionList] = useState(upcoming);

  // Mark a session as complete → auto-trigger review modal
  const handleMarkComplete = (session) => {
    setSessionList(prev => prev.filter(s => s.id !== session.id));
    // Auto-open review modal after brief delay
    setTimeout(() => setReviewModal({
      ...session,
      mentor:       session.mentor,
      mentorAvatar: session.mentorAvatar || session.mentor,
      mentorBg:     session.mentorBg,
      mentorInit:   session.mentorInit,
      type:         session.type,
      date:         session.date,
      duration:     session.duration,
    }), 400);
  };

  return (
    <div>
      <h1 className="db-page-title">My Sessions</h1><p className="db-page-sub">View and manage all your sessions.</p>
      <div className="db-tabs">{[['upcoming',`Upcoming (${sessionList.length})`],['past',`Past (${past.length})`]].map(([k,l])=>(<button key={k} className={`db-tab ${tab===k?'active':''}`} onClick={()=>setTab(k)}>{l}</button>))}</div>
      {tab==='upcoming'&&<div style={{display:'flex',flexDirection:'column',gap:14}}>
        {sessionList.map(s=>(
          <div key={s.id} className="db-card">
            <div style={{display:'flex',gap:14,alignItems:'flex-start',flexWrap:'wrap'}}>
              <Av seed={s.mentorAvatar} bg={s.mentorBg} init={s.mentorInit} size={46}/>
              <div style={{flex:1,minWidth:0}}>
                <div style={{display:'flex',gap:10,alignItems:'center',flexWrap:'wrap',marginBottom:4}}><span style={{fontWeight:800,fontSize:'.9375rem',color:'#1a1a2e'}}>{s.mentor}</span><span className={`st-badge st-${s.status}`}>{s.status}</span></div>
                <div style={{fontSize:'.82rem',color:'#6b7280',marginBottom:3}}>{s.type} · {s.duration}</div>
                <div style={{fontSize:'.84rem',fontWeight:600,color:'#374151'}}>📌 {s.topic}</div>
                <div style={{fontSize:'.78rem',color:'#9ca3af',marginTop:4}}>📅 {s.date} at {s.time} · {convertPrice(s.priceINR,currency)}</div>
              </div>
              <div style={{display:'flex',flexDirection:'column',gap:8,alignItems:'flex-end'}}>
                <MeetingJoinInline platform={s.meetingPlatform || 'google_meet'} url={s.meetLink} date={s.date} time={s.time} />
                <button className="btn btn-primary btn-sm" style={{background:'#16a34a',borderColor:'#16a34a'}}
                  onClick={() => handleMarkComplete(s)}>✅ Mark Complete</button>
                <button className="btn btn-outline btn-sm">✎ Reschedule</button>
                <button className="btn btn-outline btn-sm" style={{borderColor:'#fca5a5',color:'#dc2626'}}>✕ Cancel</button>
              </div>
            </div>
          </div>
        ))}
        {sessionList.length === 0 && (
          <div style={{textAlign:'center',padding:'48px 20px',color:'#9ca3af'}}>
            <div style={{fontSize:'2.5rem',marginBottom:12}}>🎉</div>
            <div style={{fontWeight:700,fontSize:'1rem',color:'#374151',marginBottom:6}}>All sessions completed!</div>
            <div style={{fontSize:'.84rem'}}>Check your Past tab to view completed sessions and ratings.</div>
          </div>
        )}
      </div>}
      {tab==='past'&&<div className="db-card">
        <div className="db-table-wrap"><table className="db-table"><thead><tr><th>Mentor</th><th>Type</th><th>Date</th><th>Amount</th><th>Status</th><th>Rating</th><th>Action</th></tr></thead><tbody>
          {past.map(s=>(<tr key={s.id}><td><div style={{display:'flex',alignItems:'center',gap:8}}><div className="db-mini-av" style={{background:`#${s.mentorBg}`}}>{s.mentorInit}</div>{s.mentor}</div></td><td>{s.type}</td><td>{s.date}</td><td>{convertPrice(s.priceINR,currency)}</td><td><span className={`st-badge st-${s.status}`}>{s.status}</span></td>
            <td>{s.rating ? <span style={{color:'#f59e0b',fontWeight:800}}>{'★'.repeat(s.rating)} <span style={{fontSize:'.72rem',color:'#9ca3af'}}>{s.rating}.0</span></span> : <span style={{color:'#d1d5db',fontSize:'.78rem'}}>No rating</span>}</td>
            <td><div style={{display:'flex',gap:6}}>
              {s.canRate && !reviewedIds.includes(s.id) && (
                <button className="btn btn-primary btn-sm" style={{padding:'4px 10px',fontSize:'.72rem'}}
                  onClick={()=>setReviewModal(s)}>⭐ Rate</button>
              )}
              {reviewedIds.includes(s.id) && <span style={{fontSize:'.72rem',color:'#16a34a',fontWeight:700}}>✅ Reviewed</span>}
              <button style={{fontSize:'.72rem',color:'#16a34a',background:'none',border:'none',cursor:'pointer',fontWeight:700}}>📄 Invoice</button>
            </div></td>
          </tr>))}
        </tbody></table></div>
      </div>}

      {/* ── Review Modal ── */}
      {reviewModal && (
        <ReviewModal
          session={reviewModal}
          onSubmit={(review) => {
            setReviewedIds(prev => [...prev, reviewModal.id]);
            setReviewModal(null);
          }}
          onClose={() => setReviewModal(null)}
        />
      )}
    </div>
  );
}

function Billing({ past: _past }) {
  const isMock = process.env.REACT_APP_MOCK !== 'false';
  const { currency } = useContext(AppContext);
  const statusColor = {paid:'st-paid',confirmed:'st-paid',completed:'st-paid',refunded:'st-refunded',cancelled:'st-cancelled',pending_payment:'st-pending'};
  // Build invoices from past sessions (API) or mock data
  const past = _past || (isMock ? D.pastSessions : []);
  const invoices = isMock ? (D.invoices || []) : past.map((s, i) => ({
    id:     `#INV-${String(i+1).padStart(3,'0')}`,
    date:   s.date || (s.sessionDate ? new Date(s.sessionDate).toLocaleDateString('en-IN') : '—'),
    mentor: s.mentor || 'Mentor',
    type:   s.type  || s.sessionType || '1:1 Session',
    amount: s.priceINR || 0,
    status: s.status || 'completed',
  }));
  const totalSpent = invoices.reduce((sum, inv) => sum + (inv.amount || 0), 0);
  return (
    <div>
      <h1 className="db-page-title">Billing & Invoices</h1><p className="db-page-sub">View your payment history and download invoices.</p>
      <div className="db-stats-grid-3" style={{marginBottom:24}}>
        {[{icon:'💰',label:'Total Spent',v:convertPrice(totalSpent,currency)},{icon:'✅',label:'Paid Sessions',v:invoices.filter(i=>['paid','completed'].includes(i.status)).length},{icon:'🔄',label:'Refunds',v:invoices.filter(i=>i.status==='refunded').length}].map(s=>(<div key={s.label} className="db-stat-card"><div className="dsc-icon">{s.icon}</div><div className="dsc-label">{s.label}</div><div className="dsc-value">{s.v}</div></div>))}
      </div>
      <div className="db-card">
        <div className="db-section-header"><span className="db-section-title">📋 Invoice History</span><button className="btn btn-outline btn-sm">⬇ Export All</button></div>
        <div className="db-table-wrap"><table className="db-table"><thead><tr><th>Invoice #</th><th>Date</th><th>Mentor</th><th>Session Type</th><th>Amount</th><th>Status</th><th>Action</th></tr></thead><tbody>
          {invoices.map(inv=>(<tr key={inv.id}><td style={{fontWeight:700,color:'#2563eb'}}>{inv.id}</td><td>{inv.date}</td><td>{inv.mentor}</td><td>{inv.type}</td><td style={{fontWeight:800}}>{convertPrice(inv.amount,currency)}</td><td><span className={`st-badge ${statusColor[inv.status]||'st-pending'}`}>{inv.status}</span></td></tr>))}
        </tbody></table></div>
      </div>
    </div>
  );
}

function Support() {
  const isMockSup = process.env.REACT_APP_MOCK !== 'false';
  const [tab,setTab]=useState('tickets');
  // eslint-disable-next-line no-unused-vars
  const [showNew,setShowNew]=useState(false);
  const [form,setForm]=useState({subject:'',priority:'medium',message:''});
  const statusColor={open:'st-open',in_progress:'st-in_progress',resolved:'st-resolved',closed:'st-closed'};
  const prColor={high:'pr-high',medium:'pr-medium',low:'pr-low'};
  return (
    <div>
      <h1 className="db-page-title">Support</h1><p className="db-page-sub">Get help with your sessions and account.</p>
      <div className="db-tabs">
        {[['tickets','My Tickets'],['new','Create Ticket'],['faq','FAQ']].map(([k,l])=>(<button key={k} className={`db-tab ${tab===k?'active':''}`} onClick={()=>setTab(k)}>{l}</button>))}
      </div>
      {tab==='tickets'&&<div style={{display:'flex',flexDirection:'column',gap:14}}>
        {(isMockSup ? (D.supportTickets||[]) : []).map(t=>(
          <div key={t.id} className="db-card">
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',flexWrap:'wrap',gap:12}}>
              <div><div style={{fontWeight:800,fontSize:'.9rem',color:'#1a1a2e',marginBottom:4}}>{t.subject}</div><div style={{fontSize:'.75rem',color:'#9ca3af'}}>#{t.id} · Created {t.created} · Last update {t.lastUpdate}</div></div>
              <div style={{display:'flex',gap:8,alignItems:'center'}}><span className={`st-badge ${prColor[t.priority]}`}>{t.priority}</span><span className={`st-badge ${statusColor[t.status]}`}>{t.status.replace('_',' ')}</span></div>
            </div>
            <div style={{marginTop:12,paddingTop:12,borderTop:'1px solid #f3f4f6',display:'flex',gap:10}}>
              <input className="db-field" placeholder="Add reply..." style={{flex:1,padding:'8px 12px'}}/>
              <button className="btn btn-primary btn-sm">Send</button>
            </div>
          </div>
        ))}
      </div>}
      {tab==='new'&&<div className="db-card" style={{maxWidth:620}}>
        <div className="db-section-title" style={{marginBottom:18}}>🎫 Create Support Ticket</div>
        <div style={{display:'flex',flexDirection:'column',gap:14}}>
          <div><label className="db-field-label">Subject</label><input className="db-field" placeholder="Brief description of your issue" value={form.subject} onChange={e=>setForm(f=>({...f,subject:e.target.value}))}/></div>
          <div><label className="db-field-label">Priority</label><select className="db-field" value={form.priority} onChange={e=>setForm(f=>({...f,priority:e.target.value}))}><option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option></select></div>
          <div><label className="db-field-label">Message</label><textarea className="db-field db-textarea" placeholder="Describe your issue in detail..." value={form.message} onChange={e=>setForm(f=>({...f,message:e.target.value}))}/></div>
          <div><label className="db-field-label">Attach File (optional)</label><input type="file" className="db-field" style={{padding:'8px'}}/></div>
          <button className="btn btn-primary" style={{maxWidth:200}}>Submit Ticket</button>
        </div>
      </div>}
      {tab==='faq'&&<div style={{display:'flex',flexDirection:'column',gap:10}}>
        {[{q:'How do I reschedule a session?',a:'Go to My Sessions, click on the session, and select Reschedule. You can reschedule up to 24 hours before the session start time.'},{q:'What is the refund policy?',a:'If you cancel more than 24 hours before a session, you get a full refund. Cancellations within 24 hours receive a 50% refund. No-shows are not refunded.'},{q:'How do I rate a mentor?',a:"After a session is marked completed, a Rate Now button appears in My Sessions. You can give 1–5 stars and write a testimonial."},{q:'My payment was deducted but booking is not confirmed',a:'This sometimes takes up to 10 minutes. If the issue persists after 30 minutes, please create a High Priority support ticket with your transaction ID.'}].map((f,i)=>(
          <div key={i} className="db-card"><div style={{fontWeight:800,fontSize:'.875rem',color:'#1a1a2e',marginBottom:8}}>❓ {f.q}</div><p style={{fontSize:'.84rem',color:'#6b7280',lineHeight:1.7}}>{f.a}</p></div>
        ))}
      </div>}
    </div>
  );
}

/* ── MENTEE PROFILE ──────────────────────────────────────────── */
const GOAL_OPTIONS = [
  'Get a software job', 'Switch careers into tech', 'Improve coding skills',
  'Crack interviews (FAANG)', 'Learn a specific technology', 'Build a side project',
  'Get promoted', 'Start freelancing',
];

/* ── Mentee profile edit ── */
function MenteeProfile() {
  const { user, updateUser } = useAuth();
  const isMock = process.env.REACT_APP_MOCK !== 'false';

  const [f, setF] = useState({
    name:     user?.name        || '',
    headline: user?.headline    || '',
    bio:      user?.bio         || '',
    location: user?.location    || '',
    phone:    user?.phone       || '',
    linkedin: user?.linkedinUrl || '',
    github:   user?.githubUrl   || '',
    portfolio:user?.portfolio   || '',
  });
  const [goals,  setGoals]  = useState(user?.goals || []);
  const [saving, setSaving] = useState(false);
  const [saved,  setSaved]  = useState(false);

  const completionSteps = [
    { label: 'Basic Info',    done: !!(f.name && f.headline) },
    { label: 'Bio',           done: !!f.bio },
    { label: 'Goals',         done: goals.length > 0 },
    { label: 'Social Links',  done: !!(f.linkedin || f.github) },
  ];
  const pct = Math.round((completionSteps.filter(s => s.done).length / completionSteps.length) * 100);

  const save = async () => {
    setSaving(true);
    const patch = {
      name: f.name, headline: f.headline, bio: f.bio,
      location: f.location, phone: f.phone,
      linkedinUrl: f.linkedin, githubUrl: f.github, portfolio: f.portfolio,
      goals,
      profileComplete: Math.min(100, pct * 1),
    };
    // TODO §BACKEND: PUT /api/users/me { name, headline, bio, location, phone, linkedinUrl, githubUrl, portfolio, goals }
    if (!isMock) { try { await apiClient.put('/users/me', patch); } catch (_) {} }
    updateUser(patch);
    const slug = (user?.email || '').split('@')[0].toLowerCase();
    if (slug) {
      try { localStorage.setItem(`db_mentee_profile_${slug}`, JSON.stringify({ ...patch, email: user?.email, avatarBg: user?.avatarBg, initials: user?.initials, savedAt: Date.now() })); } catch (_) {}
    }
    setTimeout(() => { setSaved(true); setSaving(false); setTimeout(() => setSaved(false), 2500); }, 800);
  };

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:18 }}>

      {/* Hero banner */}
      <div className="profile-hero" style={{ borderLeftColor:'#2563eb' }}>
        <div>
          <div className="profile-hero-av" style={{ background:`#${user?.avatarBg || '2563eb'}`, overflow:'hidden', position:'relative' }}>
            {user?.photoUrl
              ? <img src={user.photoUrl} alt={user?.name} style={{ width:'100%', height:'100%', objectFit:'cover', position:'absolute', inset:0 }} />
              : (user?.initials || user?.name?.charAt(0) || 'R')
            }
          </div>
        </div>
        <div className="profile-hero-info">
          <h1 className="profile-hero-title">My Profile</h1>
          <p className="profile-hero-sub">Complete your profile so mentors can personalise sessions just for you.</p>
          <div className="profile-hero-progress">
            <div className="profile-hero-fill" style={{ width:`${pct}%`, background:'linear-gradient(90deg,#2563eb,#7c3aed)' }} />
          </div>
          <div className="profile-hero-pct" style={{ color:'#2563eb' }}>{pct}% complete</div>
          <div className="profile-step-chips">
            {completionSteps.map(s => (
              <span key={s.label} className={`profile-step-chip ${s.done ? 'done' : 'todo'}`}>
                {s.done ? '✓ ' : '○ '}{s.label}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Section 0 — Profile Photo */}
      <ProfilePhotoUpload user={user} updateUser={updateUser} accentColor="#2563eb" />

      {/* Section 1 — Basic Info */}
      <div className="db-card profile-section-pro">
        <div className="db-section-title" style={{ marginBottom:14 }}>👤 Basic Information</div>
        <div className="db-grid-2" style={{ gap:12 }}>
          <div>
            <label className="db-field-label">Full Name</label>
            <input className="db-field" value={f.name} onChange={e => setF(x => ({ ...x, name:e.target.value }))} />
          </div>
          <div>
            <label className="db-field-label">Headline</label>
            <input className="db-field" placeholder="e.g. Aspiring Full-Stack Developer" value={f.headline} onChange={e => setF(x => ({ ...x, headline:e.target.value }))} />
          </div>
          <div>
            <label className="db-field-label">Location</label>
            <input className="db-field" placeholder="City, Country" value={f.location} onChange={e => setF(x => ({ ...x, location:e.target.value }))} />
          </div>
          <div>
            <label className="db-field-label">Phone</label>
            <input className="db-field" placeholder="+91 9876543210" value={f.phone} onChange={e => setF(x => ({ ...x, phone:e.target.value }))} />
          </div>
        </div>
        <div style={{ marginTop:12 }}>
          <label className="db-field-label">Bio</label>
          <textarea className="db-field db-textarea" rows={3} placeholder="Tell mentors about yourself and what you're working on…" value={f.bio} onChange={e => setF(x => ({ ...x, bio:e.target.value }))} />
        </div>
      </div>

      {/* Mentor match callout */}
      <div className="profile-earn-callout" style={{ background:'linear-gradient(135deg,#eff6ff,#dbeafe)', borderColor:'#bfdbfe', borderLeftColor:'#2563eb' }}>
        <div className="profile-earn-callout-ico">🎯</div>
        <div className="profile-earn-callout-text">
          <h4 style={{ color:'#1e40af' }}>A complete profile helps mentors find and connect with you faster</h4>
          <p style={{ color:'#1d4ed8' }}>Mentees with full profiles get more personalised session recommendations and better mentor matches.</p>
        </div>
      </div>

      {/* Section 2 — Learning Goals */}
      <div className="db-card profile-section-social">
        <div className="db-section-title" style={{ marginBottom:6 }}>🎯 Learning Goals</div>
        <p style={{ fontSize:'.8rem', color:'#6b7280', marginBottom:14 }}>Pick up to 5 areas you want to grow in.</p>
        <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
          {GOAL_OPTIONS.map(g => {
            const active = goals.includes(g);
            const maxed  = !active && goals.length >= 5;
            return (
              <button key={g} type="button"
                onClick={() => !maxed && setGoals(p => active ? p.filter(v => v !== g) : [...p, g])}
                style={{
                  padding:'8px 16px', borderRadius:10, cursor: maxed ? 'not-allowed' : 'pointer',
                  border:`1.5px solid ${active ? '#7c3aed' : '#e5e7eb'}`,
                  background: active ? '#f5f3ff' : '#f9fafb',
                  color: active ? '#7c3aed' : maxed ? '#d1d5db' : '#6b7280',
                  fontWeight: 700, fontSize:'.8rem', transition:'all .14s',
                }}>
                {active ? '✓ ' : ''}{g}
              </button>
            );
          })}
        </div>
        <div style={{ marginTop:12, fontSize:'.74rem', color: goals.length >= 5 ? '#7c3aed' : '#9ca3af', fontWeight:700 }}>
          {goals.length}/5 selected{goals.length >= 5 ? ' — limit reached' : ''}
        </div>
      </div>

      {/* Section 3 — Social Links */}
      <div className="db-card" style={{ borderLeft:'3px solid #9ca3af' }}>
        <div className="db-section-title" style={{ marginBottom:14 }}>🔗 Social Links</div>
        <div className="db-grid-2" style={{ gap:12 }}>
          <div>
            <label className="db-field-label">LinkedIn</label>
            <input className="db-field" placeholder="https://linkedin.com/in/…" value={f.linkedin} onChange={e => setF(x => ({ ...x, linkedin:e.target.value }))} />
          </div>
          <div>
            <label className="db-field-label">GitHub</label>
            <input className="db-field" placeholder="https://github.com/…" value={f.github} onChange={e => setF(x => ({ ...x, github:e.target.value }))} />
          </div>
          <div>
            <label className="db-field-label">Portfolio / Website</label>
            <input className="db-field" placeholder="https://yourportfolio.com" value={f.portfolio} onChange={e => setF(x => ({ ...x, portfolio:e.target.value }))} />
          </div>
        </div>
      </div>

      {/* Save row */}
      <div className="profile-save-row">
        <button className="btn btn-primary" style={{ minWidth:180 }} onClick={save} disabled={saving}>
          {saving ? 'Saving…' : saved ? '✓ Saved!' : '💾 Save Profile'}
        </button>
      </div>

    </div>
  );
}

/* ── FOLLOWING & FAVORITES ─────────────────────────────────── */
function FollowingFavorites() {
  const { currency } = useContext(AppContext);
  const { followed, favorites, getFollowerCount } = useFollow();
  const [tab, setTab] = useState('following');

  const followedMentors  = MENTORS.filter(m => followed.includes(m.id));
  const favoriteMentors  = MENTORS.filter(m => favorites.includes(m.id));
  const shown = tab === 'following' ? followedMentors : favoriteMentors;

  const MentorRow = ({ m }) => (
    <div className="session-row" style={{ padding:'14px 18px' }}>
      <div style={{ width:48, height:48, borderRadius:12, overflow:'hidden', flexShrink:0, background:`#${m.avatarBg}`, position:'relative', display:'flex', alignItems:'center', justifyContent:'center' }}>
        <img src={getAvatarUrl(m.avatarSeed, m.avatarBg, 48)} alt={m.name} style={{ width:'100%', height:'100%', objectFit:'cover' }} onError={e=>e.target.style.display='none'} />
        <span style={{ position:'absolute', fontWeight:800, fontSize:'.72rem', color:'#fff' }}>{m.initials}</span>
      </div>
      <div className="sr-info" style={{ flex:1 }}>
        <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
          <span style={{ fontWeight:800, fontSize:'.9rem' }}>{m.name}</span>
          {m.badge && <span style={{ background:'#fef3c7', color:'#92400e', fontSize:'.65rem', fontWeight:800, padding:'1px 7px', borderRadius:5 }}>{m.badge}</span>}
        </div>
        <div style={{ fontSize:'.78rem', color:'#6b7280', marginTop:2 }}>{m.role} · {m.company}</div>
        <div style={{ display:'flex', gap:12, marginTop:4, fontSize:'.75rem', color:'#9ca3af' }}>
          <span>⭐ {m.rating} ({m.reviewCount})</span>
          <span>📅 {m.sessionsCompleted} sessions</span>
          <span>👥 {(getFollowerCount(m.id)>=1000?(getFollowerCount(m.id)/1000).toFixed(1)+'k':getFollowerCount(m.id))} followers</span>
        </div>
        <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginTop:6 }}>
          {m.skills.slice(0,4).map(s=><span key={s} style={{ background:'#f3f4f6', fontSize:'.65rem', fontWeight:700, padding:'2px 8px', borderRadius:5, color:'#374151' }}>{s}</span>)}
        </div>
      </div>
      <div style={{ display:'flex', flexDirection:'column', gap:8, flexShrink:0, alignItems:'flex-end' }}>
        <div style={{ fontWeight:800, fontSize:'.9rem', color:'#16a34a' }}>{convertPrice(m.priceINR, currency)}<span style={{ fontWeight:500, fontSize:'.75rem', color:'#9ca3af' }}>/hr</span></div>
        <div style={{ display:'flex', gap:6 }}>
          <FavoriteButton mentorId={m.id} size="sm" />
          <FollowButton mentorId={m.id} size="sm" />
        </div>
        <Link to={`/mentors/${m.slug}/book`} className="btn btn-primary btn-sm" style={{ whiteSpace:'nowrap' }}>Book Session</Link>
      </div>
    </div>
  );

  return (
    <div>
      <h1 className="db-page-title">❤️ Following & Saved</h1>
      <p className="db-page-sub">Mentors you follow and have saved as favourites.</p>

      {/* Stats */}
      <div className="db-stats-grid-4" style={{ marginBottom:24 }}>
        {[
          { icon:'👥', label:'Following',  v: followedMentors.length },
          { icon:'❤️', label:'Favourites', v: favoriteMentors.length },
          { icon:'🌍', label:'In both',    v: MENTORS.filter(m=>followed.includes(m.id)&&favorites.includes(m.id)).length },
          { icon:'📅', label:'Total Mentors Available', v: MENTORS.length },
        ].map(s=>(
          <div key={s.label} className="db-stat-card">
            <div className="dsc-icon">{s.icon}</div>
            <div className="dsc-label">{s.label}</div>
            <div className="dsc-value">{s.v}</div>
          </div>
        ))}
      </div>

      {/* Tab toggle */}
      <div style={{ display:'flex', gap:8, marginBottom:16 }}>
        <button className={`btn btn-sm ${tab==='following'?'btn-primary':'btn-outline'}`} onClick={()=>setTab('following')}>
          👥 Following ({followedMentors.length})
        </button>
        <button className={`btn btn-sm ${tab==='favorites'?'btn-primary':'btn-outline'}`} onClick={()=>setTab('favorites')}>
          ❤️ Favourites ({favoriteMentors.length})
        </button>
      </div>

      {shown.length === 0 ? (
        <div className="db-empty">
          <div className="db-empty-ico">{tab==='following'?'👥':'❤️'}</div>
          <h3>{tab==='following' ? 'Not following anyone yet' : 'No favourites saved yet'}</h3>
          <p>{tab==='following'
            ? 'Follow mentors to stay updated on their events and availability.'
            : 'Tap the heart icon on any mentor card to save them as a favourite.'}</p>
          <Link to="/mentors" className="btn btn-primary" style={{ marginTop:14 }}>Browse Mentors →</Link>
        </div>
      ) : (
        <div className="db-card" style={{ overflow:'hidden' }}>
          {shown.map((m, i) => (
            <div key={m.id} style={{ borderBottom: i < shown.length-1 ? '1px solid #f3f4f6' : 'none' }}>
              <MentorRow m={m} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── MENTEE CERTIFICATIONS ──────────────────────────────────── */
function MenteeCertifications() {
  const [tab, setTab] = useState('enrolled');
  const [enrollments, setEnrollments] = useState(MY_ENROLLMENTS);
  const [submitModal, setSubmitModal] = useState(null); // { enrollment, task }
  const [githubLink, setGithubLink] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const statusColor = {
    not_started: '#9ca3af', in_progress: '#2563eb', under_review: '#f59e0b',
    approved: '#16a34a', rejected: '#ef4444', completed: '#16a34a',
  };
  const statusLabel = {
    not_started: 'Not Started', in_progress: 'In Progress', under_review: 'Under Review',
    approved: 'Approved', rejected: 'Rejected', completed: 'Completed',
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!githubLink.trim()) return;
    setSubmitting(true);
    // TODO §BACKEND: POST /api/certifications/submit { enrollmentId, taskId, githubLink }
    await new Promise(r => setTimeout(r, 900));
    setEnrollments(prev => prev.map(en => {
      if (en.id !== submitModal.enrollment.id) return en;
      const newSub = { taskId: submitModal.task.id, submittedAt: 'Just now', githubLink, status: 'under_review', feedback: '' };
      return {
        ...en,
        status: 'under_review',
        submissions: [...(en.submissions || []), newSub],
        completedTasks: [...en.completedTasks, submitModal.task.id],
      };
    }));
    setSubmitting(false);
    setSubmitModal(null);
    setGithubLink('');
  };

  const enrolled = enrollments;
  const available = COURSES.filter(c => !enrollments.find(e => e.courseId === c.id));

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1 className="db-page-title">🏆 My Certifications</h1>
        <p className="db-page-sub">Track your progress, submit projects, and earn certified credentials.</p>
      </div>

      {/* Stats */}
      <div className="db-stats-grid-4" style={{ marginBottom: 24 }}>
        {[
          { icon: '📚', label: 'Enrolled', v: enrolled.length },
          { icon: '⏳', label: 'In Progress', v: enrolled.filter(e => e.status === 'in_progress').length },
          { icon: '🔍', label: 'Under Review', v: enrolled.filter(e => e.status === 'under_review').length },
          { icon: '🎓', label: 'Completed', v: enrolled.filter(e => e.status === 'completed').length },
        ].map(s => (
          <div key={s.label} className="db-stat-card">
            <div className="dsc-icon">{s.icon}</div>
            <div className="dsc-label">{s.label}</div>
            <div className="dsc-value">{s.v}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="db-tabs" style={{ marginBottom: 20 }}>
        {['enrolled', 'browse'].map(t => (
          <button key={t} className={`db-tab${tab === t ? ' active' : ''}`} onClick={() => setTab(t)}>
            {t === 'enrolled' ? `My Courses (${enrolled.length})` : `Browse (${available.length})`}
          </button>
        ))}
      </div>

      {/* Enrolled courses */}
      {tab === 'enrolled' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {enrolled.length === 0 && (
            <div className="db-card" style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>
              <div style={{ fontSize: '2.5rem', marginBottom: 12 }}>🎓</div>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>No courses yet</div>
              <div style={{ fontSize: '.85rem' }}>Browse available certifications and enrol to get started.</div>
              <button className="btn btn-primary" style={{ marginTop: 16 }} onClick={() => setTab('browse')}>Browse Courses</button>
            </div>
          )}
          {enrolled.map(en => {
            const course = COURSES.find(c => c.id === en.courseId);
            if (!course) return null;
            const pct = Math.round((en.completedTasks.length / course.tasks.length) * 100);
            return (
              <div key={en.id} className="db-card">
                <div style={{ padding: '16px 20px', borderBottom: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{ fontSize: '1.8rem' }}>{course.icon}</span>
                    <div>
                      <div style={{ fontWeight: 800, fontSize: '.95rem' }}>{course.name}</div>
                      <div style={{ fontSize: '.78rem', color: '#6b7280' }}>Enrolled {en.enrolledDate} · {course.duration}</div>
                    </div>
                  </div>
                  <span style={{ background: statusColor[en.status] + '1a', color: statusColor[en.status], border: `1px solid ${statusColor[en.status]}40`, padding: '3px 12px', borderRadius: 20, fontSize: '.75rem', fontWeight: 700 }}>
                    {statusLabel[en.status]}
                  </span>
                </div>

                {/* Progress bar */}
                <div style={{ padding: '14px 20px', borderBottom: '1px solid #f3f4f6' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: '.8rem', color: '#6b7280' }}>
                    <span>Progress</span>
                    <span>{en.completedTasks.length}/{course.tasks.length} tasks · {pct}%</span>
                  </div>
                  <div style={{ background: '#f3f4f6', borderRadius: 8, height: 8, overflow: 'hidden' }}>
                    <div style={{ width: `${pct}%`, height: '100%', background: '#2563eb', borderRadius: 8, transition: 'width .4s' }} />
                  </div>
                </div>

                {/* Task list */}
                <div style={{ padding: '14px 20px' }}>
                  <div style={{ fontWeight: 700, fontSize: '.82rem', marginBottom: 10, color: '#374151' }}>Weekly Tasks</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {course.tasks.map(task => {
                      const done = en.completedTasks.includes(task.id);
                      const sub = en.submissions?.find(s => s.taskId === task.id);
                      const isNext = !done && course.tasks.indexOf(task) === en.completedTasks.length;
                      return (
                        <div key={task.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 14px', background: done ? '#f0fdf4' : isNext ? '#eff6ff' : '#fafafa', borderRadius: 10, border: `1px solid ${done ? '#bbf7d0' : isNext ? '#bfdbfe' : '#e5e7eb'}` }}>
                          <span style={{ fontSize: '1.1rem', marginTop: 1 }}>{done ? '✅' : isNext ? '▶️' : '⬜'}</span>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 700, fontSize: '.83rem' }}>Week {task.week}: {task.title}</div>
                            <div style={{ fontSize: '.76rem', color: '#6b7280', marginTop: 2 }}>{task.description}</div>
                            {sub && sub.feedback && (
                              <div style={{ marginTop: 6, padding: '6px 10px', background: '#f0fdf4', borderRadius: 8, fontSize: '.75rem', color: '#16a34a', fontStyle: 'italic' }}>
                                💬 Mentor: "{sub.feedback}"
                              </div>
                            )}
                          </div>
                          {isNext && (
                            <button
                              className="btn btn-primary"
                              style={{ fontSize: '.73rem', padding: '5px 12px', whiteSpace: 'nowrap' }}
                              onClick={() => { setSubmitModal({ enrollment: en, task }); setGithubLink(''); }}
                            >
                              Submit
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Certificate download */}
                {en.status === 'completed' && en.certificateUrl && (
                  <div style={{ padding: '12px 20px', borderTop: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span>🏅</span>
                    <span style={{ flex: 1, fontWeight: 700, fontSize: '.85rem' }}>Your certificate is ready!</span>
                    <a href={en.certificateUrl} target="_blank" rel="noreferrer" className="btn btn-primary" style={{ fontSize: '.78rem', padding: '6px 14px' }}>Download</a>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Browse available courses */}
      {tab === 'browse' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
          {available.length === 0 && (
            <div className="db-card" style={{ padding: 40, textAlign: 'center', color: '#9ca3af', gridColumn: '1/-1' }}>
              <div style={{ fontSize: '2.5rem', marginBottom: 12 }}>🎉</div>
              <div style={{ fontWeight: 700 }}>You're enrolled in all available courses!</div>
            </div>
          )}
          {available.map(course => (
            <div key={course.id} className="db-card" style={{ display: 'flex', flexDirection: 'column' }}>
              <div style={{ padding: '18px 20px 14px', borderBottom: '1px solid #f3f4f6' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                  <span style={{ fontSize: '2rem' }}>{course.icon}</span>
                  <div>
                    <div style={{ fontWeight: 800, fontSize: '.92rem' }}>{course.name}</div>
                    <div style={{ fontSize: '.75rem', color: '#6b7280' }}>{course.level} · {course.duration}</div>
                  </div>
                </div>
                <p style={{ fontSize: '.82rem', color: '#6b7280', margin: 0, lineHeight: 1.5 }}>{course.description}</p>
              </div>
              <div style={{ padding: '12px 20px', display: 'flex', flexWrap: 'wrap', gap: 5, borderBottom: '1px solid #f3f4f6' }}>
                {course.skills.slice(0, 4).map(s => (
                  <span key={s} style={{ background: '#eff6ff', color: '#2563eb', padding: '2px 8px', borderRadius: 20, fontSize: '.72rem', fontWeight: 600 }}>{s}</span>
                ))}
                {course.skills.length > 4 && <span style={{ color: '#9ca3af', fontSize: '.72rem', padding: '2px 6px' }}>+{course.skills.length - 4} more</span>}
              </div>
              <div style={{ padding: '12px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontWeight: 800, fontSize: '.95rem', color: '#111827' }}>₹{course.priceINR.toLocaleString()}</div>
                  <div style={{ fontSize: '.72rem', color: '#9ca3af' }}>{course.enrolledCount.toLocaleString()} enrolled</div>
                </div>
                <button className="btn btn-primary" style={{ fontSize: '.8rem', padding: '7px 16px' }}
                  onClick={() => {
                    // TODO §BACKEND: POST /api/certifications/enroll { courseId: course.id }
                    alert(`Enrollment for "${course.name}" coming soon!`);
                  }}>
                  Enrol Now
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Submit task modal */}
      {submitModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 480, padding: 28, boxShadow: '0 20px 60px rgba(0,0,0,.2)' }}>
            <div style={{ fontWeight: 800, fontSize: '1.05rem', marginBottom: 6 }}>📤 Submit Task</div>
            <div style={{ fontSize: '.83rem', color: '#6b7280', marginBottom: 20 }}>Week {submitModal.task.week}: {submitModal.task.title}</div>
            <form onSubmit={handleSubmit}>
              <label style={{ fontWeight: 700, fontSize: '.82rem', display: 'block', marginBottom: 6 }}>GitHub Repository Link *</label>
              <input
                className="db-form-input"
                type="url"
                placeholder="https://github.com/yourname/project-repo"
                value={githubLink}
                onChange={e => setGithubLink(e.target.value)}
                required
                style={{ width: '100%', marginBottom: 18 }}
              />
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setSubmitModal(null)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting ? 'Submitting…' : 'Submit for Review'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default function MenteeDashboard() {
  const { user: authUser } = useAuth();
  const isMock = process.env.REACT_APP_MOCK !== 'false';

  const [upcomingSessions, setUpcomingSessions] = useState(isMock ? (D.upcomingSessions || []) : []);
  const [pastSessions,     setPastSessions]     = useState(isMock ? (D.pastSessions     || []) : []);
  const [loading,          setLoading]          = useState(!isMock);

  useEffect(() => {
    if (isMock) return; // mock mode — use D data above, no API call

    if (!sessionStorage.getItem('db_user')) { setLoading(false); return; }

    Promise.all([
      bookingService.getUpcomingSessions(),
      bookingService.getPastSessions(),
    ]).then(([upcoming, past]) => {
      if (upcoming.sessions?.length) setUpcomingSessions(upcoming.sessions.map(normalizeBooking));
      if (past.sessions?.length)     setPastSessions(past.sessions.map(normalizeBooking));
    }).catch(err => console.error('Dashboard load error:', err))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line

  function normalizeBooking(b) {
    const mentor = b.mentorId || {};
    const user   = mentor.userId || {};
    return {
      ...b,
      id:           b._id || b.id,
      mentor:       user.firstName ? `${user.firstName} ${user.lastName}` : 'Mentor',
      mentorSlug:   mentor.slug || '',
      type:         b.sessionType || b.type || '1:1 Session',
      date:         b.sessionDate ? new Date(b.sessionDate).toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric' }) : b.date,
      time:         b.sessionTime || b.time || '',
      link:         b.meetingLink  || b.link  || '#',
      platform:     b.meetingPlatform || 'google_meet',
      status:       b.status,
      priceINR:     b.priceINR || 0,
    };
  }

  const { pathname } = useLocation();
  const sectionMap = {
    '/dashboard/mentee':'Overview',
    '/dashboard/mentee/sessions':'My Sessions',
    '/dashboard/mentee/following':'Following & Saved',
    '/dashboard/mentee/billing':'Billing',
    '/dashboard/mentee/support':'Support',
    '/dashboard/mentee/security':'Security',
    '/dashboard/mentee/profile':'My Profile',
    '/dashboard/mentee/certifications':'Certifications',
  };
  const section = Object.entries(sectionMap).find(([k])=>k===pathname)?.[1]||'Overview';
  if (loading) {
    return (
      <DashboardLayout activeSection={section}>
        <DashboardSkeleton cols={4} />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout activeSection={section}>
      <Routes>
        <Route index element={<Overview upcoming={upcomingSessions} past={pastSessions} loading={loading} user={authUser}/>}/>
        <Route path="sessions" element={<MySessions upcoming={upcomingSessions} past={pastSessions} loading={loading}/>}/>
        <Route path="following" element={<FollowingFavorites/>}/>
        <Route path="billing" element={<Billing past={pastSessions}/>}/>
        <Route path="support" element={<Support/>}/>
        <Route path="refer"     element={<MenteeReferPage/>}/>
        <Route path="security"        element={<SecurityPage/>}/>
        <Route path="profile"         element={<MenteeProfile/>}/>
        <Route path="certifications"  element={<MenteeCertifications/>}/>
      </Routes>
    </DashboardLayout>
  );

}
